import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useNoshStore } from '../lib/store';
import { useLeftEdgeSwipeDisable } from '../hooks/useLeftEdgeSwipeDisable';
import { Plus, CalendarDays, ChevronLeft, ChevronRight, Sparkles, X, Check, Save, ShoppingBag, ArrowRight } from 'lucide-react';
import MealPlanRecipeSelector from '../components/ui/meal-plan-recipe-selector';
import { ProgressiveImage } from '../components/ui/progressive-image';
import { getOptimizedImageUrl, migrateToOptimizedUrl } from '../lib/image-optimization';
import type { Recipe, FoodItem } from '../lib/store';
import { getWeekRange, getWeekDates } from '../lib/week-utils';
import ReactDOM from 'react-dom';
// import { ThemeToggle } from '../components/ui/theme-toggle';

const WeeklyPlanner = () => {
  const { recipes, user, inventory, loadMealPlan, addMealToWeek, removeMealFromWeek, loadRecipes, loadInventory, createShoppingList, loadMeals } = useNoshStore();
  const [currentWeek, setCurrentWeek] = useState(getWeekDates());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentMealPlan, setCurrentMealPlan] = useState<any>({});
  const [showRecipeSelector, setShowRecipeSelector] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{
    date: string;
    mealType: 'breakfast' | 'lunch' | 'dinner';
  } | null>(null);

  // Shopping List States
  const [servingSize, setServingSize] = useState(1);
  const [generatedShoppingList, setGeneratedShoppingList] = useState<any>(null);
  const [isGeneratingList, setIsGeneratingList] = useState(false);
  const [isSavingList, setIsSavingList] = useState(false);
  const [showShoppingListSuccess, setShowShoppingListSuccess] = useState(false);
  const [savedItemsCount, setSavedItemsCount] = useState(0);

  // Intelligent Weekly Meal Planning State
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [planGenerationStatus, setPlanGenerationStatus] = useState<string>('');

  // Meal generation modal state
  const [showMealGenerationModal, setShowMealGenerationModal] = useState(false);
  
  // Tooltip state for Generate Plan button
  const [showPlanTooltip, setShowPlanTooltip] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const generatePlanButtonRef = useRef<HTMLButtonElement>(null);

  // Desktop drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);
  const desktopCalendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Timezone-safe date to string conversion helper
  const dateToLocalString = (date: Date): string => {
    return date.getFullYear() + '-' + 
           String(date.getMonth() + 1).padStart(2, '0') + '-' + 
           String(date.getDate()).padStart(2, '0');
  };

  // Load meal plan data when week changes
  useEffect(() => {
    const loadWeekMealPlan = async () => {
      // Use centralized week calculation to ensure consistency with database
      const weekRange = getWeekRange(currentWeek[0] || new Date());
      
      const mealPlan = await loadMealPlan(weekRange.startString, weekRange.endString);
      
      if (mealPlan) {
        setCurrentMealPlan(mealPlan.meals || {});
      } else {
        setCurrentMealPlan({});
      }
    };

    loadWeekMealPlan();
  }, [currentWeek, loadMealPlan]);

  // 🔧 AUTO-REGENERATE SHOPPING LIST WHEN SERVING SIZE CHANGES
  useEffect(() => {
    // Only regenerate if we already have a generated shopping list
    if (generatedShoppingList && !isGeneratingList) {
      // Regenerate the same type of list that was previously generated
      if (generatedShoppingList.type === 'complete') {
        generateCompleteShoppingList();
      } else if (generatedShoppingList.type === 'tagged') {
        generateTaggedShoppingList();
      }
    }
  }, [servingSize]); // Only trigger when servingSize changes

  // Intelligent Weekly Meal Planning Function
  const generateWeeklyMealPlan = async () => {
    setIsGeneratingPlan(true);
    setPlanGenerationStatus('Analyzing your nutrition targets...');

    try {
      // Step 1: Calculate user's daily calorie target
      const dailyCalorieTarget = user.dailyCalorieTarget || calculateDailyCalorieTarget();
      
      if (!dailyCalorieTarget || dailyCalorieTarget <= 0) {
        throw new Error('Unable to calculate daily calorie target. Please complete your body stats in Profile.');
      }

      setPlanGenerationStatus('Loading your recipe collection...');

      // Step 2: Load and categorize recipes
      await loadRecipes(true); // Force fresh data
      
      if (!recipes || recipes.length === 0) {
        throw new Error('No recipes found. Please create some recipes first to generate a meal plan.');
      }

      // Step 3: Categorize recipes by meal type
      const categorizedRecipes = categorizeRecipesByMealType(recipes);
      console.log('🍽️ [MealPlan] Categorized recipes:', {
        breakfast: categorizedRecipes.breakfast.length,
        lunch: categorizedRecipes.lunch.length,
        dinner: categorizedRecipes.dinner.length,
        snack: categorizedRecipes.snack.length,
        dessert: categorizedRecipes.dessert.length
      });

      // Validate we have recipes for each meal type
      if (categorizedRecipes.breakfast.length === 0) {
        throw new Error('No breakfast recipes found. Please create some breakfast recipes first.');
      }
      if (categorizedRecipes.lunch.length === 0) {
        throw new Error('No lunch recipes found. Please create some lunch recipes first.');
      }
      if (categorizedRecipes.dinner.length === 0) {
        throw new Error('No dinner recipes found. Please create some dinner recipes first.');
      }

      setPlanGenerationStatus('Creating balanced weekly meal plan...');

      // Step 4: Generate meal plan for the entire week
      const weeklyPlan = generateBalancedWeeklyPlan(
        currentWeek,
        categorizedRecipes,
        dailyCalorieTarget
      );

      setPlanGenerationStatus('Saving your meal plan...');

      // Step 5: Save the meal plan to database
      let savedMeals = 0;
      const totalMeals = Object.keys(weeklyPlan).length * 3; // 3 meals per day

      for (const [dateKey, dayPlan] of Object.entries(weeklyPlan)) {
        // Save breakfast
        if (dayPlan.breakfast) {
          const success = await addMealToWeek(dateKey, 'breakfast', dayPlan.breakfast);
          if (success) savedMeals++;
        }
        
        // Save lunch
        if (dayPlan.lunch) {
          const success = await addMealToWeek(dateKey, 'lunch', dayPlan.lunch);
          if (success) savedMeals++;
        }
        
        // Save dinner
        if (dayPlan.dinner) {
          const success = await addMealToWeek(dateKey, 'dinner', dayPlan.dinner);
          if (success) savedMeals++;
        }
      }

      setPlanGenerationStatus('Refreshing meal plan display...');

      // Step 6: Reload the meal plan to update UI
      const weekRange = getWeekRange(currentWeek[0] || new Date());
      const updatedMealPlan = await loadMealPlan(weekRange.startString, weekRange.endString);
      
      if (updatedMealPlan) {
        setCurrentMealPlan(updatedMealPlan.meals || {});
      }

      // Calculate plan statistics
      const planStats = calculatePlanStatistics(weeklyPlan, dailyCalorieTarget);
      
      setPlanGenerationStatus(`✅ Plan generated! ${savedMeals} meals planned with ${planStats.avgDailyCalories} avg daily calories`);
      
      // Show success message briefly
      setTimeout(() => {
        setPlanGenerationStatus('');
      }, 3000);

    } catch (error: any) {
      console.error('❌ [MealPlan] Error generating meal plan:', error);
      setPlanGenerationStatus(`❌ ${error?.message || 'Failed to generate meal plan'}`);
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setPlanGenerationStatus('');
      }, 5000);
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  // Helper function to categorize recipes by meal type
  const categorizeRecipesByMealType = (recipes: Recipe[]): {
    breakfast: Recipe[];
    lunch: Recipe[];
    dinner: Recipe[];
    snack: Recipe[];
    dessert: Recipe[];
    other: Recipe[];
  } => {
    const categorized = {
      breakfast: [] as Recipe[],
      lunch: [] as Recipe[],
      dinner: [] as Recipe[],
      snack: [] as Recipe[],
      dessert: [] as Recipe[],
      other: [] as Recipe[]
    };

    recipes.forEach(recipe => {
      // Check mealType field first
      if (recipe.mealType) {
        const mealType = recipe.mealType.toLowerCase();
        const validMealTypes = ['breakfast', 'lunch', 'dinner', 'snack', 'dessert'] as const;
        
        if (validMealTypes.includes(mealType as any)) {
          (categorized as any)[mealType].push(recipe);
          return;
        }
      }

      // Fallback to tags analysis
      const recipeTags = (recipe.tags || []).map(tag => tag.toLowerCase());
      
      if (recipeTags.some(tag => ['breakfast', 'morning'].includes(tag))) {
        categorized.breakfast.push(recipe);
      } else if (recipeTags.some(tag => ['lunch', 'midday'].includes(tag))) {
        categorized.lunch.push(recipe);
      } else if (recipeTags.some(tag => ['dinner', 'evening'].includes(tag))) {
        categorized.dinner.push(recipe);
      } else if (recipeTags.some(tag => ['snack', 'appetizer'].includes(tag))) {
        categorized.snack.push(recipe);
      } else if (recipeTags.some(tag => ['dessert', 'sweet'].includes(tag))) {
        categorized.dessert.push(recipe);
      } else {
        // Default categorization based on calories
        if (recipe.calories < 300) {
          categorized.breakfast.push(recipe);
        } else if (recipe.calories < 600) {
          categorized.lunch.push(recipe);
        } else {
          categorized.dinner.push(recipe);
        }
      }
    });

    return categorized;
  };

  // Helper function to generate balanced weekly meal plan
  const generateBalancedWeeklyPlan = (
    weekDates: Date[],
    categorizedRecipes: {
      breakfast: Recipe[];
      lunch: Recipe[];
      dinner: Recipe[];
      snack: Recipe[];
      dessert: Recipe[];
      other: Recipe[];
    },
    dailyCalorieTarget: number
  ) => {
    const weeklyPlan: Record<string, {
      breakfast?: Recipe;
      lunch?: Recipe;
      dinner?: Recipe;
    }> = {};
    
    // Define target calorie distribution (typical healthy eating pattern)
    const calorieDistribution = {
      breakfast: 0.25, // 25% of daily calories
      lunch: 0.35,     // 35% of daily calories  
      dinner: 0.40     // 40% of daily calories
    };

    const targetCalories = {
      breakfast: Math.round(dailyCalorieTarget * calorieDistribution.breakfast),
      lunch: Math.round(dailyCalorieTarget * calorieDistribution.lunch),
      dinner: Math.round(dailyCalorieTarget * calorieDistribution.dinner)
    };

    // Enhanced tracking system for better variety
    const globalUsedRecipes = new Set<string>(); // Track all used recipes across all meal types
    const breakfastRecent = new Map<string, number>(); // Track breakfast recipe usage
    const lunchRecent = new Map<string, number>(); // Track lunch recipe usage
    const dinnerRecent = new Map<string, number>(); // Track dinner recipe usage
    const dailyUsedRecipes = new Map<number, Set<string>>(); // Track recipes used each day

    weekDates.forEach((date, dayIndex) => {
      const dateKey = dateToLocalString(date);
      const dayPlan: {
        breakfast?: Recipe;
        lunch?: Recipe;
        dinner?: Recipe;
      } = {};

      // Initialize daily tracking
      dailyUsedRecipes.set(dayIndex, new Set<string>());

      // Select breakfast first
      dayPlan.breakfast = selectBestRecipe(
        categorizedRecipes.breakfast,
        targetCalories.breakfast,
        globalUsedRecipes,
        breakfastRecent,
        dayIndex,
        'breakfast'
      ) || undefined;

      // Track breakfast selection
      if (dayPlan.breakfast) {
        globalUsedRecipes.add(dayPlan.breakfast.id);
        breakfastRecent.set(dayPlan.breakfast.id, dayIndex);
        dailyUsedRecipes.get(dayIndex)?.add(dayPlan.breakfast.id);
      }

      // Select lunch (avoid same recipe as breakfast)
      const lunchCandidates = categorizedRecipes.lunch.filter(recipe => 
        !dailyUsedRecipes.get(dayIndex)?.has(recipe.id)
      );
      
      dayPlan.lunch = selectBestRecipe(
        lunchCandidates.length > 0 ? lunchCandidates : categorizedRecipes.lunch,
        targetCalories.lunch,
        globalUsedRecipes,
        lunchRecent,
        dayIndex,
        'lunch'
      ) || undefined;

      // Track lunch selection
      if (dayPlan.lunch) {
        globalUsedRecipes.add(dayPlan.lunch.id);
        lunchRecent.set(dayPlan.lunch.id, dayIndex);
        dailyUsedRecipes.get(dayIndex)?.add(dayPlan.lunch.id);
      }

      // Select dinner (avoid same recipe as breakfast and lunch)
      const dinnerCandidates = categorizedRecipes.dinner.filter(recipe => 
        !dailyUsedRecipes.get(dayIndex)?.has(recipe.id)
      );

      dayPlan.dinner = selectBestRecipe(
        dinnerCandidates.length > 0 ? dinnerCandidates : categorizedRecipes.dinner,
        targetCalories.dinner,
        globalUsedRecipes,
        dinnerRecent,
        dayIndex,
        'dinner'
      ) || undefined;

      // Track dinner selection
      if (dayPlan.dinner) {
        globalUsedRecipes.add(dayPlan.dinner.id);
        dinnerRecent.set(dayPlan.dinner.id, dayIndex);
        dailyUsedRecipes.get(dayIndex)?.add(dayPlan.dinner.id);
      }

      weeklyPlan[dateKey] = dayPlan;
    });

    // Log final variety statistics
    const totalUniqueRecipes = globalUsedRecipes.size;
    const totalMealsPlanned = Object.values(weeklyPlan).reduce((count, dayPlan) => {
      return count + (dayPlan.breakfast ? 1 : 0) + (dayPlan.lunch ? 1 : 0) + (dayPlan.dinner ? 1 : 0);
    }, 0);

    const avgCalories = Math.round(Object.values(weeklyPlan).reduce((sum, dayPlan) => {
      return sum + (dayPlan.breakfast?.calories || 0) + (dayPlan.lunch?.calories || 0) + (dayPlan.dinner?.calories || 0);
    }, 0) / 7);

    console.log(`🎯 [MealPlan] Generated ${totalUniqueRecipes} unique recipes for ${totalMealsPlanned} meals (${Math.round((totalUniqueRecipes / totalMealsPlanned) * 100)}% variety)`);

    return weeklyPlan;
  };

  // Helper function to select best recipe based on calorie target and variety
  const selectBestRecipe = (
    availableRecipes: Recipe[],
    targetCalories: number,
    usedRecipes: Set<string>,
    recentRecipes: Map<string, number>,
    currentDay: number,
    mealType: string
  ): Recipe | null => {
    if (availableRecipes.length === 0) return null;

    // AGGRESSIVE ANTI-REPETITION: Create multiple filtering tiers
    
    // Tier 1: Never used recipes (highest priority)
    const neverUsedRecipes = availableRecipes.filter(recipe => 
      !recentRecipes.has(recipe.id)
    );
    
    // Tier 2: Recipes not used in last 3+ days (high priority)
    const notRecentRecipes = availableRecipes.filter(recipe => {
      const lastUsed = recentRecipes.get(recipe.id);
      if (lastUsed === undefined) return false; // Already in Tier 1
      return (currentDay - lastUsed) >= 3;
    });
    
    // Tier 3: Recipes not used in last 2 days (medium priority)
    const somewhatOldRecipes = availableRecipes.filter(recipe => {
      const lastUsed = recentRecipes.get(recipe.id);
      if (lastUsed === undefined) return false; // Already in Tier 1
      const daysSince = currentDay - lastUsed;
      return daysSince >= 2 && daysSince < 3;
    });
    
    // Tier 4: Recipes used yesterday (low priority)
    const yesterdayRecipes = availableRecipes.filter(recipe => {
      const lastUsed = recentRecipes.get(recipe.id);
      if (lastUsed === undefined) return false;
      return (currentDay - lastUsed) === 1;
    });
    
    // Tier 5: Recipes used today (emergency only)
    const todayRecipes = availableRecipes.filter(recipe => {
      const lastUsed = recentRecipes.get(recipe.id);
      if (lastUsed === undefined) return false;
      return (currentDay - lastUsed) === 0;
    });

    // Select the best available tier (prefer higher tiers)
    let recipesToUse: Recipe[] = [];
    let tierUsed = '';
    
    if (neverUsedRecipes.length > 0) {
      recipesToUse = neverUsedRecipes;
      tierUsed = 'never-used';
    } else if (notRecentRecipes.length > 0) {
      recipesToUse = notRecentRecipes;
      tierUsed = '3+-days-old';
    } else if (somewhatOldRecipes.length > 0) {
      recipesToUse = somewhatOldRecipes;
      tierUsed = '2-days-old';
    } else if (yesterdayRecipes.length > 0) {
      recipesToUse = yesterdayRecipes;
      tierUsed = 'yesterday';
    } else {
      // Emergency: use today's recipes (should rarely happen)
      recipesToUse = todayRecipes.length > 0 ? todayRecipes : availableRecipes;
      tierUsed = 'emergency-today';
    }

    // Removed verbose tier selection logging

    // Score recipes within the selected tier
    const scoredRecipes = recipesToUse.map(recipe => {
      let score = 0;

      // Calorie match score (0-100, higher is better)
      const calorieDiff = Math.abs(recipe.calories - targetCalories);
      const calorieScore = Math.max(0, 100 - (calorieDiff / targetCalories) * 100);
      score += calorieScore * 0.6; // Increased weight for calorie matching

      // Variety bonus (within tier, all recipes get base variety score)
      const lastUsed = recentRecipes.get(recipe.id);
      let varietyScore = 100; // Default for never used
      if (lastUsed !== undefined) {
        const daysSinceUsed = currentDay - lastUsed;
        varietyScore = Math.min(100, daysSinceUsed * 25); // 25 points per day
      }
      score += varietyScore * 0.2;

      // Preference score based on dietary preferences
      let preferenceScore = 50;
      if (recipe.dietary_preferences && user.preferences) {
        const matchingPrefs = recipe.dietary_preferences.filter(pref => 
          user.preferences.includes(pref)
        ).length;
        preferenceScore += matchingPrefs * 15;
      }
      score += preferenceScore * 0.15;

      // Difficulty score (easier recipes get slight boost for weekdays)
      let difficultyScore = 50;
      const isWeekday = currentDay < 5;
      if (isWeekday && recipe.difficulty === 'easy') {
        difficultyScore = 70;
      } else if (!isWeekday && recipe.difficulty === 'hard') {
        difficultyScore = 70;
      }
      score += difficultyScore * 0.05;

      return {
        recipe,
        score,
        calorieMatch: calorieScore,
        variety: varietyScore,
        daysSinceUsed: lastUsed !== undefined ? currentDay - lastUsed : 999
      };
    });

    // Sort by score (highest first)
    scoredRecipes.sort((a, b) => b.score - a.score);

    // REDUCED randomization: Only randomize among top 2 choices to reduce repetition
    const topChoices = scoredRecipes.slice(0, Math.min(2, scoredRecipes.length));
    const selectedChoice = topChoices[Math.floor(Math.random() * topChoices.length)];

    // Removed verbose individual meal selection logging
    
    return selectedChoice.recipe;
  };

  // Helper function to calculate plan statistics
  const calculatePlanStatistics = (
    weeklyPlan: Record<string, {
      breakfast?: Recipe;
      lunch?: Recipe;
      dinner?: Recipe;
    }>, 
    targetDailyCalories: number
  ) => {
    let totalCalories = 0;
    let totalDays = 0;

    Object.values(weeklyPlan).forEach((dayPlan) => {
      let dayCalories = 0;
      if (dayPlan.breakfast) dayCalories += dayPlan.breakfast.calories;
      if (dayPlan.lunch) dayCalories += dayPlan.lunch.calories;
      if (dayPlan.dinner) dayCalories += dayPlan.dinner.calories;
      
      totalCalories += dayCalories;
      totalDays++;
    });

    const avgDailyCalories = Math.round(totalCalories / totalDays);
    const targetAccuracy = Math.round((1 - Math.abs(avgDailyCalories - targetDailyCalories) / targetDailyCalories) * 100);

    return {
      totalCalories,
      avgDailyCalories,
      targetDailyCalories,
      targetAccuracy
    };
  };

  // Helper function to calculate daily calorie target if not set
  const calculateDailyCalorieTarget = (): number | null => {
    if (!user.age || !user.gender || !user.height || !user.weight || !user.goal || !user.activityLevel) {
      return null;
    }

    // Use Harris-Benedict equation with activity multiplier
    let bmr: number;
    
    if (user.gender === 'male') {
      bmr = 88.362 + (13.397 * user.weight) + (4.799 * user.height) - (5.677 * user.age);
    } else {
      bmr = 447.593 + (9.247 * user.weight) + (3.098 * user.height) - (4.330 * user.age);
    }

    // Activity multipliers
    const activityMultipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      very_active: 1.9
    };

    let tdee = bmr * activityMultipliers[user.activityLevel];

    // Adjust for goal
    if (user.goal === 'lose') {
      tdee -= 500; // 500 calorie deficit for 1 lb/week loss
    } else if (user.goal === 'gain') {
      tdee += 300; // 300 calorie surplus for gradual gain
    }

    return Math.round(tdee);
  };

  // Prevent browser native left edge swipe gesture on mobile
  useLeftEdgeSwipeDisable({
    pageId: 'WeeklyPlanner',
    enabled: true,
    edgeWidth: 50
  });

  function formatDate(date: Date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function getDayName(date: Date) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  function nextWeek() {
    const nextWeek = currentWeek.map(date => {
      const newDate = new Date(date);
      newDate.setDate(date.getDate() + 7);
      return newDate;
    });
    setCurrentWeek(nextWeek);
  }

  function prevWeek() {
    const prevWeek = currentWeek.map(date => {
      const newDate = new Date(date);
      newDate.setDate(date.getDate() - 7);
      return newDate;
    });
    setCurrentWeek(prevWeek);
  }

  // Helper function to get full recipe details by ID
  const getRecipeById = (recipeId: string): Recipe | null => {
    return recipes.find(recipe => recipe.id === recipeId) || null;
  };

  // Handle adding meals
  const handleAddMeal = (date: string, mealType: 'breakfast' | 'lunch' | 'dinner') => {
    setSelectedSlot({ date, mealType });
    setShowRecipeSelector(true);
  };



  // Handle recipe selection
  const handleRecipeSelect = async (recipe: Recipe) => {
    console.log('🎯 [WeeklyPlanner] handleRecipeSelect called with:', { recipe: recipe.title, selectedSlot });
    
    if (!selectedSlot) {
      console.error('❌ [WeeklyPlanner] No selectedSlot available');
      return;
    }

    console.log('🔄 [WeeklyPlanner] Calling addMealToWeek with:', {
      date: selectedSlot.date,
      mealType: selectedSlot.mealType,
      recipeId: recipe.id,
      recipeTitle: recipe.title
    });

    try {
      const success = await addMealToWeek(selectedSlot.date, selectedSlot.mealType, recipe);
      console.log('📊 [WeeklyPlanner] addMealToWeek result:', success);
      
      if (success) {
        console.log('✅ [WeeklyPlanner] Meal added successfully, reloading meal plan...');
        
        // Use the centralized week calculation to ensure consistency
        const selectedDate = new Date(selectedSlot.date);
        const weekRange = getWeekRange(selectedDate);
        
        console.log('🔄 [WeeklyPlanner] Loading meal plan for DATABASE week (where meal was saved):', { 
          selectedDate: selectedSlot.date,
          calculatedWeekStart: weekRange.startString, 
          calculatedWeekEnd: weekRange.endString 
        });
        
        const mealPlan = await loadMealPlan(weekRange.startString, weekRange.endString);
        console.log('📥 [WeeklyPlanner] Loaded meal plan:', mealPlan);
        
        if (mealPlan) {
          setCurrentMealPlan(mealPlan.meals || {});
          console.log('🎉 [WeeklyPlanner] Updated currentMealPlan state');
        } else {
          console.warn('⚠️ [WeeklyPlanner] Meal plan returned null');
        }
      } else {
        console.error('❌ [WeeklyPlanner] Failed to add meal to week');
      }
    } catch (error) {
      console.error('💥 [WeeklyPlanner] Exception in handleRecipeSelect:', error);
    }
    
    console.log('🔄 [WeeklyPlanner] Closing recipe selector modal');
    setShowRecipeSelector(false);
    setSelectedSlot(null);
  };

  // Handle removing meals
  const handleRemoveMeal = async (date: string, mealType: 'breakfast' | 'lunch' | 'dinner') => {
    console.log('🗑️ [WeeklyPlanner] handleRemoveMeal called:', { date, mealType });
    
    const success = await removeMealFromWeek(date, mealType);
    console.log('📊 [WeeklyPlanner] removeMealFromWeek result:', success);
    
    if (success) {
      console.log('✅ [WeeklyPlanner] Meal removed successfully, reloading meal plan...');
      
      // Use the centralized week calculation to ensure consistency
      const selectedDate = new Date(date);
      const weekRange = getWeekRange(selectedDate);
      
      console.log('🔄 [WeeklyPlanner] Reloading meal plan for DATABASE week:', { 
        removedDate: date,
        calculatedWeekStart: weekRange.startString, 
        calculatedWeekEnd: weekRange.endString 
      });
      
      const mealPlan = await loadMealPlan(weekRange.startString, weekRange.endString);
      if (mealPlan) {
        setCurrentMealPlan(mealPlan.meals || {});
        console.log('🎉 [WeeklyPlanner] Updated currentMealPlan after removal');
      }
    }
  };

  // Helper function to get meal for a specific date and type
  const getMealForDate = (date: Date, mealType: 'breakfast' | 'lunch' | 'dinner') => {
    const dateKey = dateToLocalString(date);
    const dayMeals = currentMealPlan[dateKey];
    return dayMeals ? dayMeals[mealType] : null;
  };

  // Shopping List Generation Functions
  const generateCompleteShoppingList = async () => {
    if (!currentMealPlan) return;
    
    setIsGeneratingList(true);
    try {
      const ingredientMap = new Map<string, {
        name: string;
        cleanName: string;
        totalAmount: number;
        unit: string;
        category: string;
        recipes: string[];
      }>();

      // Extract all ingredients from all planned meals
      Object.entries(currentMealPlan).forEach(([date, dayMeals]: [string, any]) => {
        ['breakfast', 'lunch', 'dinner'].forEach(mealType => {
          const meal = dayMeals[mealType];
          if (meal) {
            const recipe = getRecipeById(meal.recipeId);
            if (recipe) {
              const recipeServings = recipe.servings || 1;
              
              recipe.ingredients.forEach((ingredient: any) => {
                const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name;
                const ingredientAmount = typeof ingredient === 'string' ? '' : ingredient.amount || '';
                
                // Clean the ingredient name (remove brand names and normalize)
                const cleanName = cleanIngredientName(ingredientName);
                
                // Skip common pantry staples that most people already have
                if (isCommonPantryStaple(cleanName)) {
                  return; // Skip this ingredient
                }
                
                // Parse amount and unit
                const { amount, unit } = parseIngredientAmount(ingredientAmount);
                
                // Calculate amount per serving for this recipe
                const amountPerServing = amount / recipeServings;
                
                // Calculate total amount needed for user's serving size
                const totalAmountNeeded = amountPerServing * servingSize;
                
                // Convert to preferred unit for robust consolidation
                const { amount: preferredAmount, unit: preferredUnit } = convertToPreferredUnit(totalAmountNeeded, unit, cleanName);
                
                // Create smart consolidation key that groups equivalent ingredients
                const consolidationKey = createConsolidationKey(cleanName);
                
                if (ingredientMap.has(consolidationKey)) {
                  const existing = ingredientMap.get(consolidationKey)!;
                  // Add amounts since they're now in the same preferred unit
                  existing.totalAmount += preferredAmount;
                  if (!existing.recipes.includes(`${recipe.title} (${formatDate(new Date(date))})`)) {
                    existing.recipes.push(`${recipe.title} (${formatDate(new Date(date))})`);
                  }
                } else {
                  ingredientMap.set(consolidationKey, {
                    name: ingredientName,
                    cleanName,
                    totalAmount: preferredAmount,
                    unit: preferredUnit,
                    category: getIngredientCategory(cleanName),
                    recipes: [`${recipe.title} (${formatDate(new Date(date))})`]
                  });
                }
              });
            }
          }
        });
      });

      // Convert map to array and group by category
      const allIngredients = Array.from(ingredientMap.values());
      const categorizedList = groupIngredientsByCategory(allIngredients);
      
      setGeneratedShoppingList({
        type: 'complete',
        servingSize,
        categories: categorizedList,
        totalItems: allIngredients.length,
        generatedAt: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error generating shopping list:', error);
    } finally {
      setIsGeneratingList(false);
    }
  };

  const generateTaggedShoppingList = async () => {
    if (!currentMealPlan) return;
    
    setIsGeneratingList(true);
    try {
      // Load fresh inventory data
      await loadInventory(true);
      const currentInventory = inventory;
      
      const ingredientMap = new Map<string, {
        name: string;
        cleanName: string;
        totalAmount: number;
        unit: string;
        category: string;
        recipes: string[];
      }>();

      // Extract all ingredients from all planned meals (same as complete list)
      Object.entries(currentMealPlan).forEach(([date, dayMeals]: [string, any]) => {
        ['breakfast', 'lunch', 'dinner'].forEach(mealType => {
          const meal = dayMeals[mealType];
          if (meal) {
            const recipe = getRecipeById(meal.recipeId);
            if (recipe) {
              const recipeServings = recipe.servings || 1;
              
              recipe.ingredients.forEach((ingredient: any) => {
                const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name;
                const ingredientAmount = typeof ingredient === 'string' ? '' : ingredient.amount || '';
                
                // Clean the ingredient name (remove brand names and normalize)
                const cleanName = cleanIngredientName(ingredientName);
                
                // Skip common pantry staples that most people already have
                if (isCommonPantryStaple(cleanName)) {
                  return; // Skip this ingredient
                }
                
                // Check if this ingredient is already in user's inventory
                const isInInventory = currentInventory.some((inventoryItem: FoodItem) => {
                  const inventoryName = inventoryItem.name.toLowerCase();
                  const ingredientNameLower = cleanName.toLowerCase();
                  
                  // Check for exact match or partial match (both ways)
                  return inventoryName.includes(ingredientNameLower) || 
                         ingredientNameLower.includes(inventoryName) ||
                         inventoryName === ingredientNameLower;
                });
                
                // Only add to shopping list if NOT in inventory
                if (!isInInventory) {
                  // Parse amount and unit
                  const { amount, unit } = parseIngredientAmount(ingredientAmount);
                  
                  // Calculate amount per serving for this recipe
                  const amountPerServing = amount / recipeServings;
                  
                  // Calculate total amount needed for user's serving size
                  const totalAmountNeeded = amountPerServing * servingSize;
                  
                  // Convert to preferred unit for robust consolidation
                  const { amount: preferredAmount, unit: preferredUnit } = convertToPreferredUnit(totalAmountNeeded, unit, cleanName);
                  
                  // Create smart consolidation key that groups equivalent ingredients
                  const consolidationKey = createConsolidationKey(cleanName);
                  
                  if (ingredientMap.has(consolidationKey)) {
                    const existing = ingredientMap.get(consolidationKey)!;
                    // Add amounts since they're now in the same preferred unit
                    existing.totalAmount += preferredAmount;
                    if (!existing.recipes.includes(`${recipe.title} (${formatDate(new Date(date))})`)) {
                      existing.recipes.push(`${recipe.title} (${formatDate(new Date(date))})`);
                    }
                  } else {
                    ingredientMap.set(consolidationKey, {
                      name: ingredientName,
                      cleanName,
                      totalAmount: preferredAmount,
                      unit: preferredUnit,
                      category: getIngredientCategory(cleanName),
                      recipes: [`${recipe.title} (${formatDate(new Date(date))})`]
                    });
                  }
                }
              });
            }
          }
        });
      });

      // Convert map to array and group by category
      const allIngredients = Array.from(ingredientMap.values());
      const categorizedList = groupIngredientsByCategory(allIngredients);
      
      setGeneratedShoppingList({
        type: 'tagged',
        servingSize,
        categories: categorizedList,
        totalItems: allIngredients.length,
        generatedAt: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error generating smart shopping list:', error);
    } finally {
      setIsGeneratingList(false);
    }
  };

  // Helper function to check if an ingredient is a common pantry staple
  const isCommonPantryStaple = (ingredientName: string): boolean => {
    const name = ingredientName.toLowerCase().trim();
    
    // List of common pantry staples that most people have
    const commonStaples = [
      // Basic seasonings
      'salt', 'sea salt', 'table salt', 'kosher salt', 'rock salt',
      'pepper', 'black pepper', 'white pepper', 'ground pepper',
      'ground black pepper', 'freshly ground black pepper',
      
      // Common spices
      'paprika', 'cumin', 'coriander', 'turmeric', 'cinnamon',
      'nutmeg', 'allspice', 'cloves', 'bay leaves', 'bay leaf',
      'oregano', 'basil', 'thyme', 'rosemary', 'sage',
      'parsley', 'dried herbs', 'mixed herbs', 'italian seasoning',
      
      // Basic cooking essentials
      'cooking oil', 'vegetable oil', 'canola oil', 'olive oil',
      'extra virgin olive oil', 'sunflower oil',
      'white vinegar', 'apple cider vinegar', 'balsamic vinegar',
      'vanilla extract', 'vanilla essence',
      'baking powder', 'baking soda', 'bicarbonate of soda',
      'cornstarch', 'cornflour', 'plain flour', 'all purpose flour',
      
      // Basic condiments
      'tomato paste', 'tomato puree', 'ketchup', 'mustard',
      'worcestershire sauce', 'hot sauce', 'tabasco',
      
      // Common pantry items
      'sugar', 'brown sugar', 'white sugar', 'caster sugar',
      'icing sugar', 'powdered sugar', 'honey', 'maple syrup',
      'stock cubes', 'chicken stock', 'beef stock', 'vegetable stock',
      'bouillon', 'broth powder'
    ];
    
    // Check for exact matches or close variations
    return commonStaples.some(staple => {
      // Exact match
      if (name === staple) return true;
      
      // Check if ingredient name contains the staple (for variations)
      if (name.includes(staple) || staple.includes(name)) {
        // But avoid false positives for longer ingredient names
        const words = name.split(' ');
        return words.some(word => word === staple || staple.includes(word));
      }
      
      return false;
    });
  };

  // Helper function to clean ingredient names (remove brand names)
  const cleanIngredientName = (ingredientName: string): string => {
    // Enhanced brand name patterns to remove (including the specific issues from the image)
    const brandPatterns = [
      /^1111hikari\s+/i,
      /^hikari\s+/i,
      /^double phoenix\s+/i,
      /^coles\s+/i,
      /^woolworths\s+/i,
      /^iga\s+/i,
      /^farmland\s+/i,
      /^black & gold\s+/i,
      /^homebrand\s+/i,
      /^essentials\s+/i,
      /^macro\s+/i,
      /^continental\s+/i,
      /^masterfoods\s+/i,
      /^mccormick\s+/i,
      /^praise\s+/i,
      /^maggi\s+/i,
      /^knorr\s+/i,
      /^soy sauce\s+brand\s+/i,
      /^([a-zA-Z\s&]+)\s+(brand)\s+/i, // Generic "Brand Name brand" pattern
    ];

    let cleanName = ingredientName.trim();
    
    // Remove brand patterns
    for (const pattern of brandPatterns) {
      cleanName = cleanName.replace(pattern, '');
    }
    
    // Remove parenthetical brand mentions like "(Double Phoenix)"
    cleanName = cleanName.replace(/\s*\([^)]*brand[^)]*\)/gi, '');
    cleanName = cleanName.replace(/\s*\(double phoenix\)/gi, '');
    cleanName = cleanName.replace(/\s*\(coles\)/gi, '');
    cleanName = cleanName.replace(/\s*\(1111hikari\)/gi, '');
    cleanName = cleanName.replace(/\s*\(hikari\)/gi, '');
    
    // Remove egg type descriptors (cage free, free range, etc.) - they're all just eggs
    cleanName = cleanName.replace(/\b(cage free|free range|organic|pasture raised|farm fresh)\s+/gi, '');
    
    // Normalize cream types - thickened cream, heavy cream, whipping cream are all just cream
    if (cleanName.toLowerCase().includes('cream')) {
      cleanName = cleanName.replace(/\b(thickened|heavy|whipping|double|single)\s+cream/gi, 'cream');
      cleanName = cleanName.replace(/\bcream\s+(thickened|heavy|whipping|double|single)/gi, 'cream');
    }
    
    // Clean up extra spaces and capitalize first letter
    cleanName = cleanName.replace(/\s+/g, ' ').trim();
    if (cleanName.length > 0) {
      cleanName = cleanName.charAt(0).toUpperCase() + cleanName.slice(1).toLowerCase();
    }
    
    return cleanName || ingredientName; // Fallback to original if cleaning results in empty string
  };

  // Helper function to parse ingredient amounts
  const parseIngredientAmount = (amountString: string): { amount: number; unit: string } => {
    if (!amountString) return { amount: 1, unit: 'piece' }; // Default to 1 piece if no amount
    
    // Common amount patterns - Enhanced to handle descriptive units correctly
    const patterns = [
      // Standard weight measurements
      /^(\d+(?:\.\d+)?)\s*(kg|kilogram|kilograms)$/i,
      /^(\d+(?:\.\d+)?)\s*(g|gram|grams)$/i,
      
      // Standard volume measurements  
      /^(\d+(?:\.\d+)?)\s*(ml|milliliter|milliliters|millilitre|millilitres)$/i,
      /^(\d+(?:\.\d+)?)\s*(l|liter|liters|litre|litres)$/i,
      /^(\d+(?:\.\d+)?)\s*(tbsp|tablespoon|tablespoons)$/i,
      /^(\d+(?:\.\d+)?)\s*(tsp|teaspoon|teaspoons)$/i,
      /^(\d+(?:\.\d+)?)\s*(cup|cups)$/i,
      
      // Descriptive egg units (PRIORITY - match before generic patterns)
      /^(\d+)\s*(large|medium|small)\s*egg[s]?$/i,
      /^(\d+)\s*egg[s]?$/i,
      
      // Other count units
      /^(\d+(?:\.\d+)?)\s*(piece|pieces|pc|pcs)$/i,
      /^(\d+(?:\.\d+)?)\s*(clove|cloves)$/i,
      /^(\d+(?:\.\d+)?)\s*(slice|slices)$/i,
      /^(\d+(?:\.\d+)?)\s*(can|cans|tin|tins)$/i,
      /^(\d+(?:\.\d+)?)\s*(pack|packs|package|packages)$/i,
      /^(\d+(?:\.\d+)?)\s*(bottle|bottles)$/i,
      /^(\d+(?:\.\d+)?)\s*(bag|bags)$/i,
      /^(\d+(?:\.\d+)?)\s*(box|boxes)$/i,
      /^(\d+(?:\.\d+)?)\s*(bunch|bunches)$/i,
      /^(\d+(?:\.\d+)?)\s*(head|heads)$/i,
      /^(\d+(?:\.\d+)?)\s*(leaf|leaves)$/i,
      
      // Generic number + unit pattern (LAST - fallback only)
      /^(\d+(?:\.\d+)?)\s*(.*)$/i
    ];
    
    const trimmed = amountString.trim();
    
    for (const pattern of patterns) {
      const match = trimmed.match(pattern);
      if (match) {
        const amount = parseFloat(match[1]);
        let unit = match[2] ? match[2].toLowerCase() : '';
        
        // Handle descriptive egg units
        if (unit === 'large' || unit === 'medium' || unit === 'small') {
          return { amount, unit: `${unit} eggs` };
        }
        
        // Normalize standard units
        if (unit.startsWith('kg') || unit.startsWith('kilogram')) unit = 'kg';
        else if (unit.startsWith('g') || unit.startsWith('gram')) unit = 'g';
        else if (unit.startsWith('ml') || unit.startsWith('milliliter') || unit.startsWith('millilitre')) unit = 'ml';
        else if (unit.startsWith('tbsp') || unit.startsWith('tablespoon')) unit = 'tbsp';
        else if (unit.startsWith('tsp') || unit.startsWith('teaspoon')) unit = 'tsp';
        else if (unit.startsWith('cup')) unit = 'cup';
        else if (unit.startsWith('piece') || unit === 'pc' || unit === 'pcs') unit = 'piece';
        else if (unit.startsWith('clove')) unit = 'clove';
        else if (unit.startsWith('slice')) unit = 'slice';
        else if (unit.startsWith('can') || unit.startsWith('tin')) unit = 'can';
        else if (unit.startsWith('pack') || unit.startsWith('package')) unit = 'pack';
        else if (unit.startsWith('bottle')) unit = 'bottle';
        else if (unit.startsWith('bag')) unit = 'bag';
        else if (unit.startsWith('box')) unit = 'box';
        else if (unit.startsWith('bunch')) unit = 'bunch';
        else if (unit.startsWith('head')) unit = 'head';
        else if (unit.startsWith('leaf') || unit.startsWith('leaves')) unit = 'leaf';
        else if (unit.includes('mix')) unit = 'mix'; // For items like "leaf salad mix"
        else if (unit.startsWith('egg')) unit = 'piece'; // Plain "eggs" become pieces
        
        // CRITICAL FIX: Don't normalize "large", "medium", "small" followed by food items to "l"
        // Only normalize actual liter measurements
        else if ((unit.startsWith('l ') || unit === 'l' || unit.startsWith('liter') || unit.startsWith('litre')) && 
                 !unit.includes('large') && !unit.includes('leaf') && !unit.includes('lettuce')) {
          unit = 'l';
        }
        
        // For generic pattern matches with complex units, default to pieces for food items
        else if (unit.includes('egg') || unit.includes('onion') || unit.includes('tomato') || 
                 unit.includes('apple') || unit.includes('potato')) {
          unit = 'piece';
        }
        
        return { amount, unit };
      }
    }
    
    // Try to extract just a number if no pattern matches
    const numberMatch = trimmed.match(/^(\d+(?:\.\d+)?)/);
    if (numberMatch) {
      return { amount: parseFloat(numberMatch[1]), unit: 'piece' };
    }
    
    // For text-only ingredients (like "Almonds", "Honey"), default to 1 piece
    return { amount: 1, unit: 'piece' };
  };

  // Helper function to format amounts for display
  const formatAmount = (amount: number, unit: string): string => {
    if (amount === 0) return '';
    
    // Get user's measurement system preference
    const measurementSystem = user.measurementSystem || 'metric';
    
    // Round to reasonable precision
    let roundedAmount: number;
    if (amount >= 100) {
      roundedAmount = Math.round(amount);
    } else if (amount >= 10) {
      roundedAmount = Math.round(amount * 10) / 10;
    } else {
      roundedAmount = Math.round(amount * 100) / 100;
    }
    
    // Convert large gram amounts to kg (metric system)
    if (unit === 'g' && roundedAmount >= 1000 && measurementSystem === 'metric') {
      return `${(roundedAmount / 1000).toFixed(1)} kg`;
    }
    
    // Convert large ml amounts to l (metric system)
    if (unit === 'ml' && roundedAmount >= 1000 && measurementSystem === 'metric') {
      return `${(roundedAmount / 1000).toFixed(1)} l`;
    }
    
    // Convert metric to imperial if user prefers imperial
    if (measurementSystem === 'imperial') {
      switch (unit) {
        case 'g':
          if (roundedAmount >= 454) {
            return `${(roundedAmount / 453.592).toFixed(1)} lbs`;
          } else {
            return `${(roundedAmount / 28.3495).toFixed(1)} oz`;
          }
        case 'kg':
          return `${(roundedAmount * 2.20462).toFixed(1)} lbs`;
        case 'ml':
          if (roundedAmount >= 946) {
            return `${(roundedAmount / 946.353).toFixed(1)} qt`;
          } else if (roundedAmount >= 237) {
            return `${(roundedAmount / 236.588).toFixed(1)} cups`;
          } else {
            return `${(roundedAmount / 29.5735).toFixed(1)} fl oz`;
          }
        case 'l':
          return `${(roundedAmount * 0.264172).toFixed(1)} gal`;
      }
    }
    
    // Convert imperial to metric if user prefers metric
    if (measurementSystem === 'metric') {
      switch (unit) {
        case 'lbs':
          return `${(roundedAmount * 0.453592).toFixed(1)} kg`;
        case 'oz':
          return `${(roundedAmount * 28.3495).toFixed(0)} g`;
        case 'fl oz':
          return `${(roundedAmount * 29.5735).toFixed(0)} ml`;
        case 'cups':
          return `${(roundedAmount * 236.588).toFixed(0)} ml`;
        case 'qt':
          return `${(roundedAmount * 946.353).toFixed(0)} ml`;
        case 'gal':
          return `${(roundedAmount * 3.78541).toFixed(1)} l`;
      }
    }
    
    // Handle descriptive egg units
    if (unit.includes('eggs')) {
      const roundedCount = Math.ceil(roundedAmount); // Round up for count items
      return `${roundedCount} ${unit}`;
    }
    
    // For count units, don't show decimals
    if (['piece', 'pieces', 'clove', 'cloves', 'slice', 'slices', 'can', 'cans', 'pack', 'packs', 'box', 'boxes', 'bottle', 'bottles', 'bag', 'bags', 'bunch', 'bunches', 'head', 'heads', 'leaf', 'leaves', 'mix'].includes(unit)) {
      const roundedCount = Math.ceil(roundedAmount); // Round up for count items
      if (roundedCount === 1 && unit === 'piece') {
        return `${roundedCount} piece`;
      } else if (roundedCount > 1 && unit === 'piece') {
        return `${roundedCount} pieces`;
      } else if (unit === 'mix') {
        return `${roundedCount} ${unit}`;
      }
      return `${roundedCount} ${unit}`;
    }
    
    // Default formatting with unit
    if (unit) {
      return `${roundedAmount} ${unit}`;
    } else {
      // If no unit is specified, suggest a default based on measurement system
      if (measurementSystem === 'metric') {
        // Assume small amounts are grams, larger amounts might be pieces
        if (roundedAmount <= 10) {
          return `${Math.ceil(roundedAmount)} pieces`;
        } else {
          return `${roundedAmount} g`;
        }
      } else {
        // Imperial system defaults
        if (roundedAmount <= 10) {
          return `${Math.ceil(roundedAmount)} pieces`;
        } else {
          return `${(roundedAmount / 28.3495).toFixed(1)} oz`;
        }
      }
    }
  };

  // Helper function to categorize ingredients
  const getIngredientCategory = (ingredient: string): string => {
    const lowerIngredient = ingredient.toLowerCase();
    
    if (lowerIngredient.includes('chicken') || lowerIngredient.includes('beef') || 
        lowerIngredient.includes('pork') || lowerIngredient.includes('fish') ||
        lowerIngredient.includes('salmon') || lowerIngredient.includes('turkey') ||
        lowerIngredient.includes('lamb') || lowerIngredient.includes('duck') ||
        lowerIngredient.includes('prawns') || lowerIngredient.includes('shrimp')) {
      return '🥩 Meat & Fish';
    }
    if (lowerIngredient.includes('milk') || lowerIngredient.includes('cheese') || 
        lowerIngredient.includes('yogurt') || lowerIngredient.includes('egg') ||
        lowerIngredient.includes('cream') || lowerIngredient.includes('butter') ||
        lowerIngredient.includes('yoghurt')) {
      return '🥛 Dairy & Eggs';
    }
    if (lowerIngredient.includes('carrot') || lowerIngredient.includes('broccoli') || 
        lowerIngredient.includes('spinach') || lowerIngredient.includes('onion') ||
        lowerIngredient.includes('pepper') || lowerIngredient.includes('tomato') ||
        lowerIngredient.includes('potato') || lowerIngredient.includes('lettuce') ||
        lowerIngredient.includes('cucumber') || lowerIngredient.includes('celery')) {
      return '🥕 Vegetables';
    }
    if (lowerIngredient.includes('apple') || lowerIngredient.includes('banana') || 
        lowerIngredient.includes('orange') || lowerIngredient.includes('berry') ||
        lowerIngredient.includes('lemon') || lowerIngredient.includes('lime')) {
      return '🍎 Fruits';
    }
    if (lowerIngredient.includes('rice') || lowerIngredient.includes('pasta') || 
        lowerIngredient.includes('bread') || lowerIngredient.includes('flour') ||
        lowerIngredient.includes('noodle') || lowerIngredient.includes('quinoa')) {
      return '🌾 Grains & Bread';
    }
    if (lowerIngredient.includes('oil') || lowerIngredient.includes('sauce') || 
        lowerIngredient.includes('vinegar') || lowerIngredient.includes('salt') ||
        lowerIngredient.includes('pepper') || lowerIngredient.includes('garlic') ||
        lowerIngredient.includes('soy sauce') || lowerIngredient.includes('cooking wine')) {
      return '🧂 Seasonings & Condiments';
    }
    
    return '📦 Other';
  };

  // Helper function to group ingredients by category
  const groupIngredientsByCategory = (ingredients: any[]) => {
    const grouped = ingredients.reduce((acc, ingredient) => {
      const category = ingredient.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(ingredient);
      return acc;
    }, {} as Record<string, any[]>);

    // Sort categories for consistent display
    const sortedCategories = Object.keys(grouped).sort();
    return sortedCategories.map(category => ({
      name: category,
      items: grouped[category].sort((a: any, b: any) => a.cleanName.localeCompare(b.cleanName))
    }));
  };

  // Helper function to convert units to standard metric equivalents for consolidation
  const convertToStandardUnit = (amount: number, unit: string, ingredientName?: string): { amount: number; unit: string } => {
    const ingredientLower = (ingredientName || '').toLowerCase();
    
    // Special conversions for specific ingredients with different densities
    if (unit === 'tbsp' || unit === 'tsp') {
      // Honey is denser than water: 1 tbsp honey ≈ 21g, 1 tsp honey ≈ 7g
      if (ingredientLower.includes('honey')) {
        const honeyFactor = unit === 'tbsp' ? 21 : 7; // grams per tablespoon/teaspoon
        return { amount: amount * honeyFactor, unit: 'g' };
      }
      // Miso paste: 1 tbsp ≈ 18g, 1 tsp ≈ 6g
      else if (ingredientLower.includes('miso')) {
        const misoFactor = unit === 'tbsp' ? 18 : 6;
        return { amount: amount * misoFactor, unit: 'g' };
      }
      // Butter: 1 tbsp ≈ 14g, 1 tsp ≈ 5g
      else if (ingredientLower.includes('butter')) {
        const butterFactor = unit === 'tbsp' ? 14 : 5;
        return { amount: amount * butterFactor, unit: 'g' };
      }
      // For other liquid ingredients, convert to ml
      else {
        const liquidFactor = unit === 'tbsp' ? 15 : 5; // ml
        return { amount: amount * liquidFactor, unit: 'ml' };
      }
    }
    
    // Convert cooking measurements to grams/ml for proper consolidation
    const conversions: { [key: string]: { factor: number; targetUnit: string } } = {
      // Volume to ml (for liquids)
      'cup': { factor: 240, targetUnit: 'ml' },      // 1 cup = 240ml (US)
      'l': { factor: 1000, targetUnit: 'ml' },       // 1 liter = 1000ml
      
      // Weight conversions to grams
      'kg': { factor: 1000, targetUnit: 'g' },       // 1 kg = 1000g
      'oz': { factor: 28.35, targetUnit: 'g' },      // 1 oz = 28.35g
      'lbs': { factor: 453.6, targetUnit: 'g' },     // 1 lb = 453.6g
      
      // Count items stay as pieces
      'large eggs': { factor: 1, targetUnit: 'piece' },
      'medium eggs': { factor: 1, targetUnit: 'piece' },
      'small eggs': { factor: 1, targetUnit: 'piece' },
      'piece': { factor: 1, targetUnit: 'piece' },
      'clove': { factor: 1, targetUnit: 'piece' },
      'slice': { factor: 1, targetUnit: 'piece' },
      'can': { factor: 1, targetUnit: 'piece' },
      'bottle': { factor: 1, targetUnit: 'piece' },
      'pack': { factor: 1, targetUnit: 'piece' },
      'bag': { factor: 1, targetUnit: 'piece' },
      'box': { factor: 1, targetUnit: 'piece' },
      'bunch': { factor: 1, targetUnit: 'piece' },
      'head': { factor: 1, targetUnit: 'piece' }
    };

    const conversion = conversions[unit.toLowerCase()];
    if (conversion) {
      return {
        amount: amount * conversion.factor,
        unit: conversion.targetUnit
      };
    }

    // For units we don't recognize, keep original
    return { amount, unit };
  };

  // Helper function to get the preferred unit for an ingredient type
  const getPreferredUnit = (ingredientName: string): string => {
    const name = ingredientName.toLowerCase();
    
    // Liquid ingredients prefer ml/l
    if (name.includes('juice') || name.includes('milk') || name.includes('cream') || 
        name.includes('oil') || name.includes('vinegar') || name.includes('sauce') ||
        name.includes('broth') || name.includes('stock') || name.includes('wine')) {
      return 'ml';
    }
    
    // Solid ingredients by category
    if (name.includes('meat') || name.includes('fish') || name.includes('chicken') ||
        name.includes('beef') || name.includes('pork') || name.includes('tuna') ||
        name.includes('salmon') || name.includes('cheese') || name.includes('butter')) {
      return 'g';
    }
    
    // Fruits and vegetables - prefer pieces for whole items, grams for processed
    if (name.includes('lemon') || name.includes('lime') || name.includes('orange') ||
        name.includes('apple') || name.includes('banana') || name.includes('avocado') ||
        name.includes('onion') || name.includes('tomato') || name.includes('potato')) {
      return 'piece';
    }
    
    // Leafy greens and salads prefer grams
    if (name.includes('lettuce') || name.includes('spinach') || name.includes('salad') ||
        name.includes('herbs') || name.includes('parsley') || name.includes('basil')) {
      return 'g';
    }
    
    // Nuts, seeds, grains prefer grams
    if (name.includes('nuts') || name.includes('almonds') || name.includes('rice') ||
        name.includes('pasta') || name.includes('flour') || name.includes('oats')) {
      return 'g';
    }
    
    // Canned goods prefer grams
    if (name.includes('can') || name.includes('canned') || name.includes('tin')) {
      return 'g';
    }
    
    // Default to grams for most ingredients
    return 'g';
  };

  // Helper function to convert units to preferred unit for consolidation
  const convertToPreferredUnit = (amount: number, unit: string, ingredientName: string): { amount: number; unit: string } => {
    const preferredUnit = getPreferredUnit(ingredientName);
    const name = ingredientName.toLowerCase();
    
    // If already in preferred unit, return as-is
    if (unit === preferredUnit) {
      return { amount, unit };
    }
    
    // Convert to preferred unit
    if (preferredUnit === 'ml') {
      // Convert to ml
      switch (unit) {
        case 'l': return { amount: amount * 1000, unit: 'ml' };
        case 'cup': return { amount: amount * 240, unit: 'ml' };
        case 'tbsp': return { amount: amount * 15, unit: 'ml' };
        case 'tsp': return { amount: amount * 5, unit: 'ml' };
        case 'g': 
          // For liquids, assume 1g ≈ 1ml (water density)
          return { amount: amount, unit: 'ml' };
        default: return { amount, unit: preferredUnit };
      }
    } else if (preferredUnit === 'g') {
      // Convert to grams
      switch (unit) {
        case 'kg': return { amount: amount * 1000, unit: 'g' };
        case 'oz': return { amount: amount * 28.35, unit: 'g' };
        case 'lbs': return { amount: amount * 453.6, unit: 'g' };
        case 'ml':
          // For solids measured in ml, assume density of 1 (like water)
          return { amount: amount, unit: 'g' };
        case 'cup':
          // Estimate based on ingredient type
          if (name.includes('flour')) return { amount: amount * 120, unit: 'g' };
          if (name.includes('sugar')) return { amount: amount * 200, unit: 'g' };
          if (name.includes('rice')) return { amount: amount * 185, unit: 'g' };
          return { amount: amount * 150, unit: 'g' }; // Default density
        case 'tbsp':
          if (name.includes('honey') || name.includes('syrup')) return { amount: amount * 21, unit: 'g' };
          if (name.includes('butter')) return { amount: amount * 14, unit: 'g' };
          return { amount: amount * 15, unit: 'g' }; // Default
        case 'tsp':
          if (name.includes('honey') || name.includes('syrup')) return { amount: amount * 7, unit: 'g' };
          if (name.includes('butter')) return { amount: amount * 5, unit: 'g' };
          return { amount: amount * 5, unit: 'g' }; // Default
        case 'piece':
          // Convert pieces to grams based on average weights
          if (name.includes('lemon')) return { amount: amount * 60, unit: 'g' };
          if (name.includes('lime')) return { amount: amount * 30, unit: 'g' };
          if (name.includes('orange')) return { amount: amount * 150, unit: 'g' };
          if (name.includes('apple')) return { amount: amount * 180, unit: 'g' };
          if (name.includes('banana')) return { amount: amount * 120, unit: 'g' };
          if (name.includes('avocado')) return { amount: amount * 150, unit: 'g' };
          if (name.includes('onion')) return { amount: amount * 110, unit: 'g' };
          if (name.includes('tomato')) return { amount: amount * 100, unit: 'g' };
          return { amount: amount * 50, unit: 'g' }; // Default piece weight
        default: return { amount, unit: preferredUnit };
      }
    } else if (preferredUnit === 'piece') {
      // Convert to pieces
      switch (unit) {
        case 'g':
          // Convert grams to pieces based on average weights
          if (name.includes('lemon')) return { amount: amount / 60, unit: 'piece' };
          if (name.includes('lime')) return { amount: amount / 30, unit: 'piece' };
          if (name.includes('orange')) return { amount: amount / 150, unit: 'piece' };
          if (name.includes('apple')) return { amount: amount / 180, unit: 'piece' };
          if (name.includes('banana')) return { amount: amount / 120, unit: 'piece' };
          if (name.includes('avocado')) return { amount: amount / 150, unit: 'piece' };
          if (name.includes('onion')) return { amount: amount / 110, unit: 'piece' };
          if (name.includes('tomato')) return { amount: amount / 100, unit: 'piece' };
          return { amount: amount / 50, unit: 'piece' }; // Default
        case 'ml':
          // For liquid measured items that should be pieces, convert based on typical serving sizes
          if (name.includes('lemon') || name.includes('lime')) return { amount: amount / 15, unit: 'piece' }; // 1 tbsp juice per piece
          return { amount: amount / 30, unit: 'piece' }; // Default liquid to piece conversion
        default: return { amount, unit: preferredUnit };
      }
    }
    
    // Fallback - return original
    return { amount, unit };
  };

  // Helper function to create smart consolidation key for ingredients
  const createConsolidationKey = (cleanName: string): string => {
    const name = cleanName.toLowerCase().trim();
    
    // STEP 1: Aggressive normalization - remove ALL descriptive words first
    let normalizedName = name
      .replace(/\b(fresh|dried|frozen|organic|raw|cooked|large|medium|small|extra|ripe|topless|canned|can|tinned|tin|loose|thick|heavy|light|double|single|whole|chopped|diced|sliced|minced|crushed|ground|powdered|free\s*range|cage\s*free|farm\s*fresh|hikari|1111hikari|brand|premium|select|choice|quality)\b/g, '')
      .replace(/\b(\d+)\s*(leaf|leaves)\b/g, '') // Remove "4 leaf" type descriptors
      .replace(/\s+/g, ' ')
      .trim();

    // STEP 2: Core ingredient mapping - map to canonical forms
    const coreIngredientMappings: { [key: string]: string } = {
      // Dairy & Eggs - ALL variations map to single canonical form
      'cream': 'cream',
      'thickened cream': 'cream',
      'heavy cream': 'cream', 
      'whipping cream': 'cream',
      'double cream': 'cream',
      'single cream': 'cream',
      'sour cream': 'sour_cream', // Keep separate from regular cream
      
      'egg': 'eggs',
      'eggs': 'eggs',
      
      'butter': 'butter',
      'unsalted butter': 'butter',
      'salted butter': 'butter',
      
      'cheese': 'cheese',
      'cheddar': 'cheddar_cheese',
      'cheddar cheese': 'cheddar_cheese',
      
      // Proteins
      'tuna': 'tuna',
      'can tuna': 'tuna',
      'canned tuna': 'tuna',
      'tuna can': 'tuna',
      'tinned tuna': 'tuna',
      
      'chicken': 'chicken',
      'chicken breast': 'chicken_breast',
      'chicken thigh': 'chicken_thigh',
      
      // Produce  
      'lemon': 'lemon',
      'lemons': 'lemon',
      'lemon juice': 'lemon_juice', // Keep juice separate from whole lemons
      
      'lime': 'lime', 
      'limes': 'lime',
      'lime juice': 'lime_juice',
      
      'pineapple': 'pineapple',
      'topless pineapple': 'pineapple',
      'cored pineapple': 'pineapple',
      
      'avocado': 'avocado',
      'avocados': 'avocado',
      
      'onion': 'onion',
      'onions': 'onion',
      'yellow onion': 'onion',
      'white onion': 'onion', 
      'red onion': 'red_onion', // Keep red onion separate
      
      'tomato': 'tomato',
      'tomatoes': 'tomato',
      
      'ginger': 'ginger',
      'ginger root': 'ginger',
      
      'garlic': 'garlic',
      'garlic clove': 'garlic',
      
      // Salads & Mixes
      'salad': 'salad_mix',
      'salad mix': 'salad_mix',
      'leaf salad': 'salad_mix',
      'leaf salad mix': 'salad_mix',
      'lettuce mix': 'salad_mix',
      'mixed salad': 'salad_mix',
      
      // Specialty items
      'miso': 'miso_paste',
      'miso paste': 'miso_paste',
      'white miso': 'miso_paste',
      'white miso paste': 'miso_paste',
      
      'honey': 'honey',
      'raw honey': 'honey',
      'organic honey': 'honey',
      
      // Mushrooms
      'mushroom': 'mushrooms',
      'mushrooms': 'mushrooms',
      'button mushroom': 'mushrooms',
      'button mushrooms': 'mushrooms',
      'cup mushroom': 'mushrooms',
      'cup mushrooms': 'mushrooms',
      'white mushroom': 'mushrooms',
      'white mushrooms': 'mushrooms'
    };

    // STEP 3: Check for exact canonical mappings first
    if (coreIngredientMappings[normalizedName]) {
      return coreIngredientMappings[normalizedName];
    }

    // STEP 4: Check for partial word matches to catch variations
    for (const [pattern, canonical] of Object.entries(coreIngredientMappings)) {
      const patternWords = pattern.split(' ');
      const nameWords = normalizedName.split(' ');
      
      // If main keyword matches, use canonical form
      const mainKeyword = patternWords[patternWords.length - 1]; // Last word is usually the main ingredient
      if (nameWords.includes(mainKeyword) || normalizedName.includes(pattern)) {
        return canonical;
      }
    }

    // STEP 5: Final fallback - create normalized underscore key
    let finalKey = normalizedName
      .replace(/s$/, '') // Remove trailing 's' for plurals
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '') // Remove any non-alphanumeric characters
      .replace(/_+/g, '_') // Collapse multiple underscores
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
    
    return finalKey || 'unknown_ingredient';
  };

   // Helper function to get display name for consolidated ingredients
   const getDisplayName = (cleanName: string, unit: string): string => {
     const name = cleanName.toLowerCase();
     
     // Use smart display names for consolidated ingredients based on canonical keys
     const displayNames: { [key: string]: string } = {
       // Dairy & Eggs
       'cream': 'Cream',
       'sour_cream': 'Sour cream',
       'eggs': 'Eggs',
       'butter': 'Butter',
       'cheese': 'Cheese',
       'cheddar_cheese': 'Cheddar cheese',
       
       // Proteins
       'tuna': 'Tuna',
       'chicken': 'Chicken',
       'chicken_breast': 'Chicken breast',
       'chicken_thigh': 'Chicken thigh',
       
       // Produce
       'lemon': 'Lemons',
       'lemon_juice': 'Lemon juice',
       'lime': 'Limes',
       'lime_juice': 'Lime juice',
       'pineapple': 'Pineapple',
       'avocado': 'Avocados',
       'onion': 'Onions',
       'red_onion': 'Red onions',
       'tomato': 'Tomatoes',
       'ginger': 'Fresh ginger',
       'garlic': 'Garlic',
       
       // Salads & Mixes
       'salad_mix': 'Salad mix',
       
       // Specialty items
       'miso_paste': 'Miso paste',
       'honey': 'Honey',
       
       // Mushrooms
       'mushrooms': 'Mushrooms'
     };

     // Check for exact canonical key matches first
     if (displayNames[name]) {
       return displayNames[name];
     }
     
     // Check for partial matches (for keys with underscores)
     for (const [key, displayName] of Object.entries(displayNames)) {
       if (name === key || name.includes(key) || key.includes(name)) {
         return displayName;
       }
     }

     // Fallback: clean up the name and capitalize it properly
     let fallbackName = cleanName
       .replace(/_/g, ' ') // Convert underscores to spaces
       .split(' ')
       .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
       .join(' ');
     
     return fallbackName;
   };

  // Function to remove item from generated shopping list
  const removeItemFromShoppingList = (categoryName: string, itemIndex: number) => {
    if (!generatedShoppingList) return;
    
    // Create a deep copy of the shopping list
    const updatedShoppingList = {
      ...generatedShoppingList,
      categories: generatedShoppingList.categories.map((category: any) => {
        if (category.name === categoryName) {
          // Remove the item at the specified index
          const updatedItems = category.items.filter((_: any, index: number) => index !== itemIndex);
          return {
            ...category,
            items: updatedItems
          };
        }
        return category;
      }).filter((category: any) => category.items.length > 0) // Remove empty categories
    };
    
    // Update total items count
    const newTotalItems = updatedShoppingList.categories.reduce((total: number, category: any) => 
      total + category.items.length, 0
    );
    updatedShoppingList.totalItems = newTotalItems;
    
    // Update the state
    setGeneratedShoppingList(updatedShoppingList);
  };

  const saveShoppingList = async () => {
    if (!generatedShoppingList) return;
    
    setIsSavingList(true);
    try {
      // Prepare the shopping list data
      const listTitle = generatedShoppingList.type === 'complete' 
        ? `Complete Shopping List - ${new Date().toLocaleDateString()}`
        : `Smart Shopping List - ${new Date().toLocaleDateString()}`;
      
      const listNotes = `Generated from meal plan for serving size: ${servingSize} ${servingSize === 1 ? 'person' : 'people'}`;
      
      // Flatten all items from all categories
      const allItems = generatedShoppingList.categories.flatMap((category: any) =>
        category.items.map((item: any) => ({
          name: getDisplayName(item.cleanName, item.unit),
          quantity: parseFloat(formatAmount(item.totalAmount, item.unit).split(' ')[0]) || item.totalAmount,
          unit: item.unit || null,
          category: category.name
        }))
      );
      
      // Create the shopping list in database
      const savedList = await createShoppingList({
        title: listTitle,
        notes: listNotes,
        items: allItems
      });
      
      if (savedList) {
        // Show custom success modal and clear the generated list
        setSavedItemsCount(allItems.length);
        setShowShoppingListSuccess(true);
        setGeneratedShoppingList(null);
      } else {
        // Could show a custom error modal here instead
        console.error('Failed to save shopping list');
      }
    } catch (error) {
      console.error('Error saving shopping list:', error);
      // Could show a custom error modal here instead
    } finally {
      setIsSavingList(false);
    }
  };

  const handleViewShoppingList = () => {
    setShowShoppingListSuccess(false);
    window.location.href = '/shopping';
  };

  const handleCloseSuccessModal = () => {
    setShowShoppingListSuccess(false);
  };

  // Desktop drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!desktopCalendarRef.current || isMobile) return;
    
    setIsDragging(true);
    setDragStartX(e.pageX);
    setDragStartScrollLeft(desktopCalendarRef.current.scrollLeft);
    
    // Prevent text selection while dragging
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !desktopCalendarRef.current || isMobile) return;
    
    e.preventDefault();
    const x = e.pageX;
    const walk = (x - dragStartX) * 2; // Multiply by 2 for faster scrolling
    desktopCalendarRef.current.scrollLeft = dragStartScrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Load data when component mounts
  useEffect(() => {
    const weekStart = currentWeek[0];
    const weekEnd = currentWeek[6];
    loadMealPlan(
      weekStart.toISOString().split('T')[0], 
      weekEnd.toISOString().split('T')[0]
    );
    loadRecipes();
    loadMeals();
  }, [loadMealPlan, loadRecipes, loadMeals, currentWeek]);

  // Update button position when tooltip shows
  useEffect(() => {
    if (showPlanTooltip && generatePlanButtonRef.current) {
      const updatePosition = () => {
        if (generatePlanButtonRef.current) {
          setButtonRect(generatePlanButtonRef.current.getBoundingClientRect());
        }
      };
      
      // Initial position update
      updatePosition();
      
      // Throttled scroll/resize handlers to reduce excessive calls
      let timeoutId: NodeJS.Timeout;
      const throttledUpdate = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(updatePosition, 100); // Throttle to 100ms
      };
      
      window.addEventListener('scroll', throttledUpdate, { passive: true });
      window.addEventListener('resize', throttledUpdate, { passive: true });
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('scroll', throttledUpdate);
        window.removeEventListener('resize', throttledUpdate);
      };
    }
  }, [showPlanTooltip]); // Only depend on tooltip visibility, not button ref

  return (
    <div className="max-w-6xl mx-auto">
      {/* Mobile Simple Text Header - Match Inventory Design */}
      {isMobile && (
        <div className="px-4 mb-6 pt-6">
          <h1 className="text-2xl font-heading font-bold text-gray-900 mb-1">Weekly Meal Planner</h1>
          <p className="text-gray-600 text-sm">Plan your meals for the week</p>
        </div>
      )}

      {/* Desktop Header - only show on desktop */}
      {!isMobile && (
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold">Weekly Meal Planner</h1>
            {/* Plan Generation Status */}
            {planGenerationStatus && (
              <div className="mt-1 text-sm">
                <span className={`
                  ${planGenerationStatus.startsWith('✅') ? 'text-green-600' : ''}
                  ${planGenerationStatus.startsWith('❌') ? 'text-red-600' : ''}
                  ${!planGenerationStatus.startsWith('✅') && !planGenerationStatus.startsWith('❌') ? 'text-blue-600' : ''}
                `}>
                  {planGenerationStatus}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {/* <ThemeToggle /> */}
            <div className="relative">
              <Button 
                onClick={generateWeeklyMealPlan}
                disabled={isGeneratingPlan}
                onMouseEnter={() => setShowPlanTooltip(true)}
                onMouseLeave={() => setShowPlanTooltip(false)}
                className="relative overflow-hidden bg-primary hover:bg-primary/90 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group"
                ref={generatePlanButtonRef}
              >
                {/* Animated background shimmer */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                
                {/* Content */}
                <div className="relative flex items-center gap-2">
                  <div className="relative">
                    <Sparkles size={18} className="relative z-10" />
                    {/* Sparkle effects */}
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full opacity-80" />
                    <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-pink-300 rounded-full opacity-80" />
                  </div>
                  <span className="font-medium">{isGeneratingPlan ? 'Generating...' : 'Generate Plan'}</span>
                  <Sparkles size={14} className="animate-pulse text-yellow-200" />
                </div>
                
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-md bg-primary/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-110" />
              </Button>

              {/* Tooltip */}
              {showPlanTooltip && buttonRect && ReactDOM.createPortal(
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  style={{
                    position: 'fixed',
                    top: buttonRect.bottom + 8,
                    left: Math.max(16, Math.min(window.innerWidth - 320, buttonRect.left + (buttonRect.width / 2) - 160)),
                    zIndex: 10000
                  }}
                >
                  {/* Arrow pointing up - positioned relative to button center */}
                  <div 
                    className="w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45 absolute -top-1.5 shadow-sm"
                    style={{
                      left: Math.max(16, Math.min(304, buttonRect.left + (buttonRect.width / 2) - (Math.max(16, Math.min(window.innerWidth - 320, buttonRect.left + (buttonRect.width / 2) - 160)))))
                    }}
                  ></div>
                  
                  {/* Tooltip content */}
                  <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-80 relative">
                                      <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-primary rounded-lg flex items-center justify-center">
                        <Sparkles size={12} className="text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm">AI Weekly Meal Planner</h3>
                    </div>
                      
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Generate a personalized weekly meal plan using your existing recipes, tailored to your calorie targets and nutritional goals.
                      </p>
                      
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-700 mb-1">Smart planning considers:</p>
                        <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <span>🎯</span>
                            <span>Daily calorie targets</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span>⚖️</span>
                            <span>Balanced nutrients</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span>🍽️</span>
                            <span>Meal type distribution</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span>🔄</span>
                            <span>Recipe variety</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-1 border-t border-gray-100">
                        <p className="text-xs text-gray-500 leading-relaxed">
                          💡 <strong>Tip:</strong> The more recipes you have, the more variety your meal plan will include!
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>,
                document.body
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Full-Width Calendar View */}
      {isMobile ? (
        <div className="bg-white rounded-xl overflow-hidden">
          {/* Week Navigation */}
          <div className="flex justify-between items-center px-4 py-4 border-b border-gray-200">
            <Button variant="outline" size="sm" onClick={prevWeek}>
              <ChevronLeft size={16} />
            </Button>
            
            <div className="flex items-center">
              <CalendarDays size={18} className="mr-2 text-primary" />
              <span className="font-medium text-sm">
                {formatDate(currentWeek[0])} - {formatDate(currentWeek[6])}
              </span>
            </div>
            
            <Button variant="outline" size="sm" onClick={nextWeek}>
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Mobile Generate Button */}
          <div className="px-4 py-3 border-b border-gray-200">
            <button 
              className="w-full py-3 px-6 bg-primary hover:bg-primary/90 text-white font-medium rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              onClick={generateWeeklyMealPlan}
              disabled={isGeneratingPlan}
            >
              <Sparkles size={18} className="flex-shrink-0" />
              <span>{isGeneratingPlan ? 'Generating...' : 'Generate Plan'}</span>
            </button>
            
            {/* Plan Generation Status */}
            {planGenerationStatus && (
              <div className="mt-2 text-xs text-center">
                <span className={`
                  ${planGenerationStatus.startsWith('✅') ? 'text-green-600' : ''}
                  ${planGenerationStatus.startsWith('❌') ? 'text-red-600' : ''}
                  ${!planGenerationStatus.startsWith('✅') && !planGenerationStatus.startsWith('❌') ? 'text-blue-600' : ''}
                `}>
                  {planGenerationStatus}
                </span>
              </div>
            )}
          </div>

          {/* Horizontal Scrolling Calendar */}
          <div className="overflow-x-auto">
            <div className="flex min-w-max">
              {/* Meal Type Labels Column - Vertical Text */}
              <div className="flex flex-col bg-gray-50 border-r border-gray-200" style={{ width: '60px' }}>
                {/* Empty header space - Match date header height */}
                <div className="h-12 border-b border-gray-200"></div>
                
                {['Breakfast', 'Lunch', 'Dinner'].map((mealType) => (
                  <div key={mealType} className="h-36 flex items-center justify-center px-1 border-b border-gray-200 last:border-b-0">
                    <div className="transform -rotate-90 whitespace-nowrap text-xs font-medium text-gray-700">
                      {mealType}
                    </div>
                  </div>
                ))}
              </div>

              {/* Days Grid */}
              {currentWeek.map((date, dayIndex) => {
                const dateKey = dateToLocalString(date);
                
                return (
                  <div key={dayIndex} className="flex flex-col min-w-[140px] w-[140px] border-r border-gray-200 last:border-r-0">
                    {/* Day Header */}
                    <div className="h-12 p-2 border-b border-gray-200 bg-transparent">
                      <div className="text-center flex items-center justify-center h-full">
                        <span className="text-xs font-medium text-gray-900 mr-1">{getDayName(date)}</span>
                        <span className="text-xs text-gray-600">{formatDate(date)}</span>
                      </div>
                    </div>
                    
                    {/* Meal Slots */}
                    {['breakfast', 'lunch', 'dinner'].map((mealType) => {
                      const meal = getMealForDate(date, mealType as 'breakfast' | 'lunch' | 'dinner');
                      const recipe = meal ? getRecipeById(meal.recipeId) : null;
                      
                      return (
                        <div key={mealType} className="h-36 p-1.5 border-b border-gray-200 last:border-b-0 bg-transparent">
                          {meal ? (
                            <div className="h-full flex flex-col bg-transparent">
                              {/* Recipe Thumbnail - TRUE Perfect Square Container */}
                              <div className="relative w-28 h-28 mx-auto rounded-md overflow-hidden mb-1" style={{ width: '112px', height: '112px' }}>
                                <ProgressiveImage
                                  src={recipe ? migrateToOptimizedUrl(recipe, 'meal-slot-mobile') : '/img/small 1.png'}
                                  alt={meal.recipeName}
                                  className="w-full h-full object-cover !bg-transparent"
                                  placeholder="/img/small 1.png"
                                />
                                {/* Remove button - Perfect Circle */}
                                <button
                                  onClick={() => handleRemoveMeal(dateKey, mealType as 'breakfast' | 'lunch' | 'dinner')}
                                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 shadow-md flex items-center justify-center border-0 p-0"
                                  style={{ minWidth: '20px', minHeight: '20px' }}
                                >
                                  <X size={12} className="text-white" />
                                </button>
                              </div>
                              
                              {/* Recipe Info - Compact */}
                              <div className="flex-1 text-xs">
                                <div className="font-medium line-clamp-1 mb-0.5 text-[11px]">{meal.recipeName}</div>
                                <div className="text-gray-600 text-[10px] flex justify-between">
                                  <span>⏱️{meal.cookTime}m</span>
                                  <span>🔥{meal.calories}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <button 
                              onClick={() => handleAddMeal(dateKey, mealType as 'breakfast' | 'lunch' | 'dinner')}
                              className="h-full w-full flex flex-col items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/5 transition-all duration-200 rounded-md border border-dashed border-gray-300 hover:border-primary/30"
                            >
                              <Plus size={14} />
                              <span className="text-[10px] mt-1">Add</span>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        // Desktop View - Unchanged
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex justify-between items-center mb-6">
            <Button variant="outline" size="icon" onClick={prevWeek}>
              <ChevronLeft size={18} />
            </Button>
            
            <div className="flex items-center">
              <CalendarDays size={20} className="mr-2 text-primary" />
              <span className="font-medium">
                {formatDate(currentWeek[0])} - {formatDate(currentWeek[6])}
              </span>
            </div>
            
            <Button variant="outline" size="icon" onClick={nextWeek}>
              <ChevronRight size={18} />
            </Button>
          </div>

          <div 
            className="overflow-x-auto"
            ref={desktopCalendarRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ 
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none'
            }}
          >
            <div className="min-w-[900px]">
              <div className="flex">
                {/* Empty space for header alignment */}
                <div className="w-12"></div>
                
                {/* Header Row with Days */}
                <div className="grid grid-cols-7 gap-4 mb-6 flex-1">
                  {currentWeek.map((date, index) => (
                    <div key={index} className="text-center">
                      <div className="font-medium text-lg">{getDayName(date)}</div>
                      <div className="text-sm text-text-secondary">{formatDate(date)}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex">
                {/* Meal Type Labels Column - Vertical Text like mobile */}
                <div className="flex flex-col w-12">
                  {['Breakfast', 'Lunch', 'Dinner'].map((mealType) => (
                    <div key={mealType} className="h-40 flex items-center justify-center">
                      <div className="transform -rotate-90 whitespace-nowrap text-sm font-medium text-gray-700">
                        {mealType}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Days Grid - Vertical Layout */}
                <div className="grid grid-cols-7 gap-4 flex-1">
                  {currentWeek.map((date, dayIndex) => {
                    const dateKey = dateToLocalString(date);
                    
                    return (
                      <div key={dayIndex} className="flex flex-col space-y-3">
                        {/* Breakfast */}
                        {(() => {
                          const meal = getMealForDate(date, 'breakfast');
                          const recipe = meal ? getRecipeById(meal.recipeId) : null;
                          
                          return (
                            <div className="bg-muted rounded-lg p-3 h-40 flex flex-col relative group">
                              {meal ? (
                                <>
                                  {/* Delete button - positioned on card, not image */}
                                  <button
                                    onClick={() => handleRemoveMeal(dateKey, 'breakfast')}
                                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 shadow-lg flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 z-10"
                                  >
                                    <X size={14} className="text-white" />
                                  </button>
                                  
                                  <div className="flex flex-col h-full">
                                    {/* Recipe Thumbnail */}
                                    <div className="relative mb-2 rounded-lg overflow-hidden flex-1">
                                      <ProgressiveImage
                                        src={recipe ? migrateToOptimizedUrl(recipe, 'meal-slot-desktop') : '/img/small 1.png'}
                                        alt={meal.recipeName}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 !bg-transparent"
                                        placeholder="/img/small 1.png"
                                      />
                                    </div>
                                  
                                    {/* Recipe Info */}
                                    <div className="flex flex-col">
                                      <div className="text-sm font-medium line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                        {meal.recipeName}
                                      </div>
                                      <div className="flex justify-between text-xs text-text-secondary">
                                        <span className="flex items-center gap-1">
                                          ⏱️ {meal.cookTime}m
                                        </span>
                                        <span className="flex items-center gap-1">
                                          🔥 {meal.calories}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <button 
                                  onClick={() => handleAddMeal(dateKey, 'breakfast')}
                                  className="flex items-center justify-center h-full w-full text-text-secondary hover:text-primary hover:bg-primary/10 transition-all duration-200 rounded-lg border-2 border-dashed border-transparent hover:border-primary/30"
                                >
                                  <div className="flex flex-col items-center gap-2">
                                    <Plus size={20} />
                                    <span className="text-xs font-medium">Add Meal</span>
                                  </div>
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Lunch */}
                        {(() => {
                          const meal = getMealForDate(date, 'lunch');
                          const recipe = meal ? getRecipeById(meal.recipeId) : null;
                          
                          return (
                            <div className="bg-muted rounded-lg p-3 h-40 flex flex-col relative group">
                              {meal ? (
                                <>
                                  {/* Delete button - positioned on card, not image */}
                                  <button
                                    onClick={() => handleRemoveMeal(dateKey, 'lunch')}
                                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 shadow-lg flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 z-10"
                                  >
                                    <X size={14} className="text-white" />
                                  </button>
                                  
                                  <div className="flex flex-col h-full">
                                    {/* Recipe Thumbnail */}
                                    <div className="relative mb-2 rounded-lg overflow-hidden flex-1">
                                      <ProgressiveImage
                                        src={recipe ? migrateToOptimizedUrl(recipe, 'meal-slot-desktop') : '/img/small 1.png'}
                                        alt={meal.recipeName}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 !bg-transparent"
                                        placeholder="/img/small 1.png"
                                      />
                                    </div>
                                  
                                    {/* Recipe Info */}
                                    <div className="flex flex-col">
                                      <div className="text-sm font-medium line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                        {meal.recipeName}
                                      </div>
                                      <div className="flex justify-between text-xs text-text-secondary">
                                        <span className="flex items-center gap-1">
                                          ⏱️ {meal.cookTime}m
                                        </span>
                                        <span className="flex items-center gap-1">
                                          🔥 {meal.calories}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <button 
                                  onClick={() => handleAddMeal(dateKey, 'lunch')}
                                  className="flex items-center justify-center h-full w-full text-text-secondary hover:text-primary hover:bg-primary/10 transition-all duration-200 rounded-lg border-2 border-dashed border-transparent hover:border-primary/30"
                                >
                                  <div className="flex flex-col items-center gap-2">
                                    <Plus size={20} />
                                    <span className="text-xs font-medium">Add Meal</span>
                                  </div>
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Dinner */}
                        {(() => {
                          const meal = getMealForDate(date, 'dinner');
                          const recipe = meal ? getRecipeById(meal.recipeId) : null;
                          
                          return (
                            <div className="bg-muted rounded-lg p-3 h-40 flex flex-col relative group">
                              {meal ? (
                                <>
                                  {/* Delete button - positioned on card, not image */}
                                  <button
                                    onClick={() => handleRemoveMeal(dateKey, 'dinner')}
                                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 shadow-lg flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 z-10"
                                  >
                                    <X size={14} className="text-white" />
                                  </button>
                                  
                                  <div className="flex flex-col h-full">
                                    {/* Recipe Thumbnail */}
                                    <div className="relative mb-2 rounded-lg overflow-hidden flex-1">
                                      <ProgressiveImage
                                        src={recipe ? migrateToOptimizedUrl(recipe, 'meal-slot-desktop') : '/img/small 1.png'}
                                        alt={meal.recipeName}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 !bg-transparent"
                                        placeholder="/img/small 1.png"
                                      />
                                    </div>
                                  
                                    {/* Recipe Info */}
                                    <div className="flex flex-col">
                                      <div className="text-sm font-medium line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                        {meal.recipeName}
                                      </div>
                                      <div className="flex justify-between text-xs text-text-secondary">
                                        <span className="flex items-center gap-1">
                                          ⏱️ {meal.cookTime}m
                                        </span>
                                        <span className="flex items-center gap-1">
                                          🔥 {meal.calories}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <button 
                                  onClick={() => handleAddMeal(dateKey, 'dinner')}
                                  className="flex items-center justify-center h-full w-full text-text-secondary hover:text-primary hover:bg-primary/10 transition-all duration-200 rounded-lg border-2 border-dashed border-transparent hover:border-primary/30"
                                >
                                  <div className="flex flex-col items-center gap-2">
                                    <Plus size={20} />
                                    <span className="text-xs font-medium">Add Meal</span>
                                  </div>
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 bg-white rounded-xl shadow-soft py-6">
        <div className="flex items-center gap-3 mb-6 px-6">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg">
            <ShoppingCart size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-heading font-semibold text-gray-900">Shopping List Generator</h2>
              
              {/* Information Icon with Tooltip */}
              <div className="relative group">
                <div className="w-5 h-5 rounded-full bg-gray-400 hover:bg-gray-500 flex items-center justify-center cursor-help transition-colors">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                
                {/* Pro Tips Tooltip - Mobile Responsive */}
                <div className="absolute right-0 md:left-0 top-full mt-2 w-72 md:w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-gray-200 p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Pro Tips</h4>
                      <ul className="text-sm text-gray-600 space-y-1.5">
                        <li>• Complete list includes all recipe ingredients automatically</li>
                        <li>• Smart list checks your inventory and excludes items you already have</li>
                        <li>• Both lists automatically exclude common pantry staples (salt, pepper, oil, etc.)</li>
                        <li>• Both lists consolidate duplicate ingredients and combine quantities</li>
                        <li>• Adjust serving size to scale quantities for your household</li>
                        <li>• All lists are organized by category for easier shopping</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Tooltip Arrow - Responsive positioning */}
                  <div className="absolute -top-1 right-4 md:left-4 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600">Create your shopping list from planned meals</p>
          </div>
        </div>
        

        
        <div className="grid md:grid-cols-2 gap-4 mb-6 px-6">
          {/* All Ingredients Option */}
          <div className="group border-2 border-gray-200 hover:border-primary/30 rounded-xl p-5 transition-all duration-200 hover:shadow-md">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Complete Ingredients List</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Generate a comprehensive shopping list from all ingredients used in your planned recipes
                </p>
              </div>
            </div>
            <button 
              className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-2xl transition-all duration-75 ease-out hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:active:scale-100"
              onClick={generateCompleteShoppingList}
              disabled={isGeneratingList || !currentMealPlan || Object.keys(currentMealPlan).length === 0}
            >
              {isGeneratingList ? 'Generating...' : 'Generate Complete List'}
            </button>
          </div>

          {/* Smart Shopping List Option */}
          <div className="group border-2 border-gray-200 hover:border-gray-400 rounded-xl p-5 transition-all duration-200 hover:shadow-md">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Smart Shopping List</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Generate shopping list excluding ingredients you already have in your inventory
                </p>
              </div>
            </div>
            <button 
              className="w-full bg-gray-700 hover:bg-gray-800 text-white font-medium py-3 px-4 rounded-2xl transition-all duration-75 ease-out hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:active:scale-100"
              onClick={generateTaggedShoppingList}
              disabled={isGeneratingList || !currentMealPlan || Object.keys(currentMealPlan).length === 0}
            >
              {isGeneratingList ? 'Generating...' : 'Generate Smart List'}
            </button>
          </div>
        </div>


        
        {/* Compact Serving Size Selector - Only show when list is generated */}
        {generatedShoppingList && (
          <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 mx-6">
            <div className="flex items-center justify-center gap-4">
              <span className="text-sm font-medium text-gray-700">Serving Size:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setServingSize(Math.max(1, servingSize - 1))}
                  className="w-6 h-6 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors text-sm"
                  disabled={servingSize <= 1}
                >
                  <span className="font-medium">-</span>
                </button>
                <div className="min-w-[2.5rem] text-center">
                  <span className="text-lg font-bold text-green-600">{servingSize}</span>
                </div>
                <button
                  onClick={() => setServingSize(Math.min(10, servingSize + 1))}
                  className="w-6 h-6 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors text-sm"
                  disabled={servingSize >= 10}
                >
                  <span className="font-medium">+</span>
                </button>
              </div>
              <span className="text-xs text-gray-500">
                {servingSize === 1 ? 'person' : 'people'}
              </span>
            </div>
          </div>
        )}

        {/* Generated List Display Area */}
        {generatedShoppingList ? (
          <div className="p-3 md:p-6 bg-white">
            {/* Mobile Layout - Stack vertically */}
            {isMobile ? (
              <div className="mb-3">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                  {generatedShoppingList.type === 'complete' ? '🛒 Complete Shopping List' : '🎯 Smart Shopping List'}
                </h3>
                {generatedShoppingList.type === 'tagged' && (
                  <p className="text-sm text-gray-600 mb-2">(Inventory Filtered)</p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveShoppingList}
                    disabled={isSavingList}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-2.5 px-3 rounded-xl transition-all duration-75 ease-out hover:shadow-lg active:scale-95 disabled:active:scale-100 flex items-center justify-center gap-2 text-sm"
                  >
                    {isSavingList ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save List
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setGeneratedShoppingList(null)}
                    disabled={isSavingList}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 disabled:text-gray-400 font-medium py-2.5 px-3 rounded-xl transition-all duration-75 ease-out hover:shadow-md active:scale-95 disabled:active:scale-100 text-sm"
                  >
                    Clear List
                  </button>
                </div>
              </div>
            ) : (
              /* Desktop Layout - Side by side */
              <div className="flex items-center justify-between mb-4">
                <div>
                                  <h3 className="font-semibold text-gray-900 text-lg">
                  {generatedShoppingList.type === 'complete' ? '🛒 Complete Shopping List' : '🎯 Smart Shopping List (Inventory Filtered)'}
                </h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={saveShoppingList}
                    disabled={isSavingList}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white border-green-600 flex items-center gap-2"
                  >
                    {isSavingList ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save List
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGeneratedShoppingList(null)}
                    disabled={isSavingList}
                    className="text-gray-500 hover:text-gray-700 disabled:text-gray-400"
                  >
                    Clear List
                  </Button>
                </div>
              </div>
            )}
            
            <div className="space-y-2 md:space-y-4">
              {generatedShoppingList.categories.map((category: any, index: number) => (
                <div key={index} className="bg-white rounded-lg p-3 md:p-4 border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2 md:mb-3 flex items-center gap-2">
                    {category.name}
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {category.items.length} items
                    </span>
                  </h4>
                  <div className="grid gap-0.5 md:gap-1">
                    {category.items.map((item: any, itemIndex: number) => (
                      <div key={itemIndex} className="flex items-center justify-between py-2 px-2 md:px-3 hover:bg-gray-50 rounded-lg transition-colors group">
                        <div className="flex items-center gap-2 md:gap-3 flex-1">
                          {/* Quick Delete Button - Perfect Circle on Mobile */}
                          <button
                            onClick={() => removeItemFromShoppingList(category.name, itemIndex)}
                            className="w-5 h-5 min-w-[20px] min-h-[20px] rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all duration-200 opacity-60 hover:opacity-100 group-hover:opacity-100 flex-shrink-0"
                            title="Remove item"
                          >
                            <svg 
                              className="w-3 h-3 text-white" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                              strokeWidth={2.5}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                            </svg>
                          </button>
                          
                          {/* Item Name */}
                          <div className="font-medium text-gray-900 text-sm md:text-base">
                            {getDisplayName(item.cleanName, item.unit)}
                          </div>
                        </div>
                        
                        {/* Amount */}
                        <div className="text-sm font-semibold text-gray-700 ml-2 md:ml-4">
                          {formatAmount(item.totalAmount, item.unit)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            {generatedShoppingList.totalItems === 0 && (
              <div className="text-center py-8 text-gray-500">
                <ShoppingCart size={40} className="mx-auto mb-3 text-gray-400" />
                <p>No items found for this list type.</p>
                <p className="text-sm mt-1">
                  {generatedShoppingList.type === 'tagged' 
                    ? 'Try the complete list or check that your inventory is up to date.'
                    : 'Add some meals to your weekly plan first.'
                  }
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center bg-gray-50/50 mx-6">
            <ShoppingCart size={40} className="mx-auto mb-3 text-gray-400" />
            <h3 className="font-medium text-gray-900 mb-1">Ready to Generate</h3>
            <p className="text-sm text-gray-600">
              {!currentMealPlan ? 'Add some meals to your weekly plan, then generate your shopping list.' : 'Your shopping list will appear here after generation'}
            </p>
          </div>
        )}
      </div>

      {/* Recipe Selection Modal */}
      <MealPlanRecipeSelector
        isOpen={showRecipeSelector}
        onClose={() => {
          setShowRecipeSelector(false);
          setSelectedSlot(null);
        }}
        onSelectRecipe={handleRecipeSelect}
        selectedMealType={selectedSlot?.mealType}
        selectedDate={selectedSlot?.date}
      />

      {/* Shopping List Success Modal */}
      {showShoppingListSuccess && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleCloseSuccessModal}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
          >
            {/* Success Header */}
            <div className="p-6 pb-4 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Check size={32} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Shopping List Saved!
              </h3>
              <p className="text-gray-600">
                {savedItemsCount} items have been saved to your shopping list.
              </p>
            </div>
            
            {/* Actions */}
            <div className="px-6 pb-6 pt-2">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleCloseSuccessModal}
                  className="flex-1 py-3 text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Close
                </Button>
                <Button
                  onClick={handleViewShoppingList}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2"
                >
                  <ShoppingBag size={18} />
                  View List
                  <ArrowRight size={16} />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Additional components used in the WeeklyPlanner component
const ShoppingCart = ({ size, className }: { size: number, className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <circle cx="8" cy="21" r="1"></circle>
    <circle cx="19" cy="21" r="1"></circle>
    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
  </svg>
);

export default WeeklyPlanner;